import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { supabase, toError } from '../supabase';
import type { Papers } from '../supabase';
import { comparePublicPapersNewestFirst } from '../publicPaperOrdering';

export type Paper = Papers & {
    subfield?: { id: number; name: string; slug: string; field_id: number } | null;
};

interface PageData {
    papers: Paper[];
    nextCursor: string | null;
}

export const usePublicPapersInfiniteQuery = (userId?: string, initialPapers: Paper[] = [], enabled = true) => {
    const query = useInfiniteQuery({
        queryKey: ['papers', 'public', 'infinite', userId || 'anonymous'],
        queryFn: async ({ pageParam }: { pageParam: string | null }) => {
            // Your existing fetch logic
            let query = supabase
                .from('papers')
                .select(`
                    *,
                    subfield:subfields(id, name, slug, field_id)
                `)
                .eq('status', 'completed')
                .eq('is_private', false);

            if (userId) {
                query = query.neq('user_id', userId);
            }

            if (pageParam) {
                query = query.lt('created_at', pageParam);
            }

            const { data, error } = await query
                .order('created_at', { ascending: false })
                .limit(20);

            if (error) throw toError(error);

            return {
                papers: data || [],
                nextCursor: data?.length === 20 ? data[data.length - 1].created_at : null
            };
        },

        getNextPageParam: (lastPage) => lastPage.nextCursor,
        initialPageParam: null,

        // CRITICAL: Don't refetch if we have static data
        enabled: typeof window !== 'undefined' && enabled,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        staleTime: 1000 * 60 * 10, // 10 minutes - static data is good enough
    });

    // Simple deduplication - return live data when available, static otherwise
    const papers = useMemo(() => {
        // If no live data yet, use static papers
        if (!query.data) {
            return initialPapers;
        }

        // Once we have live data, deduplicate
        const paperMap = new Map<string, Paper>();

        // Add live papers (these are the freshest)
        query.data.pages.forEach(page => {
            page.papers.forEach(paper => {
                paperMap.set(paper.id, paper);
            });
        });

        // Add any static papers not in live data (for completeness)
        initialPapers.forEach(paper => {
            if (!paperMap.has(paper.id)) {
                paperMap.set(paper.id, paper);
            }
        });

        // Return sorted array
        return Array.from(paperMap.values()).sort(comparePublicPapersNewestFirst);
    }, [query.data, initialPapers]);

    // Return a safe default during SSG
    if (typeof window === 'undefined') {
        return {
            ...query,
            data: undefined,
            fetchNextPage: () => Promise.resolve(),
            hasNextPage: false,
            isFetchingNextPage: false,
        };
    }

    return {
        ...query,
        data: {
            pages: [{ papers, nextCursor: null }],
            pageParams: [null]
        }
    };
};
